import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  apiMe,
  apiLogin,
  apiLogout,
  setUnauthorizedHandler,
  type AuthUser,
} from '../api';
import { setShowcaseMode, clearSaveErrors } from '../lib/saveStatus';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (u: AuthUser | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // When a business API call returns 401, force logout state (the app
    // will redirect to /login via the guard).
    setUnauthorizedHandler(() => {
      setShowcaseMode(false);
      setUser(null);
    });
    apiMe()
      .then((u) => {
        setShowcaseMode(!!u?.is_showcase);
        setUser(u);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const u = await apiLogin(username, password);
    // Read-only showcase (demo) flag: cuts the autosave + unlocks the full
    // sidebar. Must be set BEFORE the app starts loading band data.
    setShowcaseMode(!!u?.is_showcase);
    setUser(u);
    // Drop any failure recorded while nobody was signed in (the band list is
    // fetched on mount and 401s before the session exists). Without this the
    // stale "⚠️ Not saved" banner survives the login and looks like a fresh
    // failure on every sign-in. (2026-09-24)
    clearSaveErrors();
    // Signal ProfileProvider to (re)load bands now that the session exists.
    window.dispatchEvent(new Event('bandz-auth-login'));
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      /* ignore */
    }
    // Clear the active band selection so the next arrival lands on the
    // neutral band list (no band-scoped data loaded automatically).
    try {
      localStorage.removeItem('bandz-active-profile');
    } catch {
      /* ignore */
    }
    setShowcaseMode(false);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an <AuthProvider>');
  return ctx;
}
